import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  Menu, 
  X, 
  Home, 
  Briefcase, 
  Info, 
  Mail,
  Image,
  Languages,
  Settings
} from 'lucide-react'
import { useStore, useMenuState } from '../../store/useStore'

const Navbar = () => {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const { isMenuOpen, toggleMenu, closeMenu } = useMenuState()
  const { language, setLanguage, isAuthenticated, user } = useStore()
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { path: '/', label: t('nav.home'), icon: Home },
    { path: '/services', label: t('nav.services'), icon: Briefcase },
    { path: '/gallery', label: t('nav.gallery'), icon: Image },
    { path: '/about', label: t('nav.about'), icon: Info },
    { path: '/contact', label: t('nav.contact'), icon: Mail },
  ]

  const toggleLanguage = () => {
    const newLang = language === 'fr' ? 'en' : 'fr'
    setLanguage(newLang)
    i18n.changeLanguage(newLang)
  }

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-primary-500 shadow-lg' : 'bg-primary-500'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-lg overflow-hidden"
              >
                <img 
                  src="https://raw.githubusercontent.com/BrandBoostApp/image-/main/2.jpg" 
                  alt="Logo Résidences Masséna"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <div>
                <h1 className="text-xl font-display font-bold text-white">
                  Résidences Masséna Inc.
                </h1>
                <p className="text-xs text-white/80">La Maison du Bien-être</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="relative px-4 py-2 rounded-lg transition-all group"
                  >
                    <span className={`text-sm font-medium ${
                      isActive ? 'text-white' : 'text-white/90 hover:text-white'
                    }`}>
                      {link.label}
                    </span>
                    {isActive && (
                      <motion.div
                        layoutId="navbar-indicator"
                        className="absolute inset-0 bg-white/10 rounded-lg"
                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                  </Link>
                )
              })}
            </div>

            {/* Action Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleLanguage}
                className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg flex items-center space-x-2 transition-all"
              >
                <Languages className="w-4 h-4 text-white" />
                <span className="text-sm font-medium text-white uppercase">{language}</span>
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={toggleMenu}
              className="lg:hidden bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-all"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-white" />
              ) : (
                <Menu className="w-6 h-6 text-white" />
              )}
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMenu}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="fixed top-0 right-0 bottom-0 w-80 glass-card border-l border-white/20 z-50 lg:hidden overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-xl font-display font-bold gradient-text">
                    Menu
                  </h2>
                  <button
                    onClick={closeMenu}
                    className="glass-btn p-2"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <nav className="space-y-2">
                  {navLinks.map((link) => {
                    const Icon = link.icon
                    const isActive = location.pathname === link.path
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={closeMenu}
                        className={`flex items-center space-x-3 p-4 rounded-xl transition-all ${
                          isActive
                            ? 'bg-white/20 text-white'
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{link.label}</span>
                      </Link>
                    )
                  })}
                </nav>

                <div className="mt-8 pt-8 border-t border-white/20">
                  <button
                    onClick={toggleLanguage}
                    className="w-full glass-btn p-4 flex items-center justify-between"
                  >
                    <span className="flex items-center space-x-3">
                      <Languages className="w-5 h-5" />
                      <span className="font-medium">Language</span>
                    </span>
                    <span className="text-sm font-bold uppercase">{language}</span>
                  </button>

                  {isAuthenticated && user?.role === 'admin' && (
                    <Link
                      to="/admin"
                      onClick={closeMenu}
                      className="w-full glass-btn p-4 flex items-center space-x-3 mt-3"
                    >
                      <Settings className="w-5 h-5" />
                      <span className="font-medium">Admin Panel</span>
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

export default Navbar

